/**
 * Deduces who a face is from the moments it is alone on screen while one
 * transcript speaker is talking (PLAT-12143 experiment). Every such moment is a
 * vote for "this face is that speaker"; a face is assigned once its votes
 * clearly favour one speaker, so a single cutaway to the listener can't flip
 * it. Each face track is counted on its own, so a person re-tracked at every
 * cut still gets each of their tracks assigned.
 */

import { extractSpeakerIntervals } from './speakerIntervalUtils';

import type { TranscriptWord } from './speakerIntervalUtils';
import type { FaceDetection } from './types';

/**
 * Fewest solo moments before a face with votes for more than one speaker is
 * assigned. A face whose votes all agree needs only one: face data is sampled
 * sparsely, so a track that lives for a single shot may be seen only once.
 */
export const MIN_SOLO_VOTES = 3;
/**
 * A single unanimous vote only counts when solo shots are this common across
 * the story. In a constant wide shot one stray solo frame is noise, not a
 * close-up.
 */
export const MIN_SOLO_SHARE_FOR_SINGLE_VOTES = 0.1;
/** The top speaker needs this many times the runner-up's votes. */
export const SOLO_DOMINANCE = 3;
/** Faces smaller than this share of the frame's largest face don't count as company. */
const MIN_COMPANY_AREA_RATIO = 0.25;
/**
 * A solo moment is part of a montage — someone narrating over shots of other
 * people — when this many different faces are each alone on screen within
 * `MONTAGE_WINDOW_SEC` of it. Two people cutting back and forth only ever
 * alternate two faces.
 */
export const MONTAGE_MIN_FACES = 3;
export const MONTAGE_WINDOW_SEC = 2;

export interface SoloShotDeduction {
  /** face_label → transcript speaker_label, for faces with clear evidence. */
  faceToSpeaker: Record<string, string>;
  /** face_label → speaker_label → solo moments while that speaker talked. */
  votes: Record<string, Record<string, number>>;
  /** face_label → media time of its first solo moment, for spot checks. */
  firstSoloTime: Record<string, number>;
  /** Sampled frames with any face, and how many of them were solo + speech. */
  frames: number;
  soloFrames: number;
  /** Media times of solo moments dropped as montage. */
  montageTimes: number[];
}

export function deduceFacesFromSoloShots(
  transcript: ReadonlyArray<TranscriptWord>,
  detections: ReadonlyArray<Pick<FaceDetection, 'time' | 'label' | 'box'>>,
): SoloShotDeduction {
  const intervals = extractSpeakerIntervals(transcript)
    .filter(interval => interval.speakerLabel !== 'unknown')
    .sort((a, b) => a.startTime - b.startTime);
  const sorted = [...detections].sort((a, b) => a.time - b.time);

  const moments: Array<{ time: number; face: string; speaker: string }> = [];
  let frames = 0;
  let intervalIndex = 0;

  for (let start = 0; start < sorted.length; ) {
    // One sampled frame = every detection sharing its timestamp.
    const time = sorted[start].time;
    let end = start;
    while (end < sorted.length && sorted[end].time === time) end++;
    const frame = sorted.slice(start, end);
    start = end;
    frames++;

    const largest = Math.max(...frame.map(({ box }) => box[2] * box[3]));
    const faces = frame.filter(
      ({ box }) => box[2] * box[3] >= largest * MIN_COMPANY_AREA_RATIO,
    );
    if (new Set(faces.map(({ label }) => label)).size !== 1) continue;

    while (
      intervalIndex < intervals.length &&
      intervals[intervalIndex].endTime < time
    ) {
      intervalIndex++;
    }
    const interval = intervals[intervalIndex];
    if (!interval || interval.startTime > time) continue;

    moments.push({
      time,
      face: faces[0].label,
      speaker: interval.speakerLabel,
    });
  }

  const votes: Record<string, Record<string, number>> = {};
  const firstSoloTime: Record<string, number> = {};
  const montageTimes: number[] = [];
  let windowStart = 0;
  let windowEnd = 0;
  for (const moment of moments) {
    while (moments[windowStart].time < moment.time - MONTAGE_WINDOW_SEC) {
      windowStart++;
    }
    while (
      windowEnd < moments.length &&
      moments[windowEnd].time <= moment.time + MONTAGE_WINDOW_SEC
    ) {
      windowEnd++;
    }
    const nearbyFaces = new Set(
      moments.slice(windowStart, windowEnd).map(({ face }) => face),
    );
    if (nearbyFaces.size >= MONTAGE_MIN_FACES) {
      montageTimes.push(moment.time);
      continue;
    }

    firstSoloTime[moment.face] ??= moment.time;
    const faceVotes = (votes[moment.face] ??= {});
    faceVotes[moment.speaker] = (faceVotes[moment.speaker] ?? 0) + 1;
  }

  const soloFrames = moments.length - montageTimes.length;
  const trustSingleVotes =
    frames > 0 && soloFrames / frames >= MIN_SOLO_SHARE_FOR_SINGLE_VOTES;
  const faceToSpeaker: Record<string, string> = {};
  for (const [faceLabel, bySpeaker] of Object.entries(votes)) {
    const [top, runnerUp] = Object.entries(bySpeaker).sort(
      (a, b) => b[1] - a[1],
    );
    const enoughVotes = top[1] >= MIN_SOLO_VOTES || trustSingleVotes;
    if (enoughVotes && (!runnerUp || top[1] >= runnerUp[1] * SOLO_DOMINANCE)) {
      faceToSpeaker[faceLabel] = top[0];
    }
  }

  return {
    faceToSpeaker,
    votes,
    firstSoloTime,
    frames,
    soloFrames,
    montageTimes,
  };
}
